import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import {
  FaArrowLeft,
  FaPrint,
  FaUndoAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaReceipt,
  FaCheckCircle,
  FaBoxOpen,
  FaShieldAlt,
  FaHistory,
} from "react-icons/fa";

export default function RefundOrderDetailsView(props) {
  const routeParams = useParams();
  const orderId = props.match?.params?.order_id || routeParams?.order_id;

  const [loading, setLoading] = useState(true);
  const [refundDetails, setRefundDetails] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [orderGrandTotal, setOrderGrandTotal] = useState(0);
  const [totalRefunded, setTotalRefunded] = useState(0);
  const [remainingRefundable, setRemainingRefundable] = useState(0);
  const [refundTransactions, setRefundTransactions] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    document.title = `Refund Details #${orderId} — Admin`;
    fetchRefundDetails();
  }, [orderId]);

  const fetchRefundDetails = () => {
    setLoading(true);
    axios.get(`/api/fetch-refund-order-details/${orderId}`).then((res) => {
      if (res.data.status === 200) {
        setRefundDetails(res.data.data.refundDetails || []);
        setTotalCost(res.data.data.total_products_cost || 0);
        setOrderGrandTotal(res.data.data.order_grand_total || res.data.data.total_products_cost || 0);
        setTotalRefunded(res.data.data.total_refunded || 0);
        setRemainingRefundable(res.data.data.remaining_refundable || 0);
        setRefundTransactions(res.data.data.refund_transactions || []);
        setTimeline(res.data.data.timeline || []);
        const orderData = Array.isArray(res.data.data.order)
          ? res.data.data.order[0]
          : res.data.data.order;
        setOrder(orderData);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-muted mt-2 small">Loading refund transaction details...</p>
      </div>
    );
  }

  const customerName = order
    ? `${order.firstname || ""} ${order.lastname || ""}`.trim() || "Customer"
    : "Customer";

  const isOrderFullyRefunded =
    order?.order_status === "Refunded" ||
    Number(order?.status) === 5 ||
    (Number(orderGrandTotal) > 0 && Number(remainingRefundable) <= 0.01 && Number(totalRefunded) > 0);

  const refundedCount = refundDetails.filter(
    (item) => Number(item.is_item_refunded) === 1 || isOrderFullyRefunded
  ).length;

  return (
    <div className="container-fluid px-0">
      {/* Top Breadcrumb and Actions */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-1 small">
              <li className="breadcrumb-item">
                <Link to="/admin/refundView" className="text-decoration-none text-muted">
                  Refunds & Returns
                </Link>
              </li>
              <li className="breadcrumb-item active text-dark fw-semibold" aria-current="page">
                Order #{order?.tracking_no || orderId}
              </li>
            </ol>
          </nav>
          <div className="d-flex align-items-center gap-2">
            <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>
              Refund Details
            </h3>
            {isOrderFullyRefunded ? (
              <span className="admin-badge admin-badge-danger">
                <FaUndoAlt size={10} /> Fully Refunded
              </span>
            ) : (
              <span className="admin-badge admin-badge-warning">
                <FaUndoAlt size={10} /> Partially Refunded ({refundedCount} of {refundDetails.length} items)
              </span>
            )}
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            onClick={() => window.print()}
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3"
          >
            <FaPrint /> Print Receipt
          </button>
          <Link
            to="/admin/refundView"
            className="btn btn-primary d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 shadow-sm rounded-3"
          >
            <FaArrowLeft /> Back to List
          </Link>
        </div>
      </div>

      <div className="row g-4">
        {/* Left Column: Line Items & Summary */}
        <div className="col-lg-8">
          {/* Refunded Items Card */}
          <div className="admin-card mb-4">
            <div className="admin-card-header bg-white d-flex align-items-center justify-content-between">
              <h5 className="admin-card-title d-flex align-items-center gap-2">
                <FaBoxOpen className={isOrderFullyRefunded ? "text-danger" : "text-primary"} /> Order Items ({refundedCount} of {refundDetails.length} Refunded)
              </h5>
              {isOrderFullyRefunded ? (
                <span className="badge bg-danger bg-opacity-10 text-danger px-2.5 py-1 fw-semibold">
                  Fully Restocked / Adjusted
                </span>
              ) : (
                <span className="badge bg-warning bg-opacity-10 text-warning px-2.5 py-1 fw-semibold">
                  Partial Return ({refundedCount}/{refundDetails.length})
                </span>
              )}
            </div>

            <div className="table-responsive">
              <table className="admin-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: "50px" }}>#</th>
                    <th>Product Details</th>
                    <th className="text-center">Qty</th>
                    <th className="text-end">Unit Price</th>
                    <th className="text-end">Discount</th>
                    <th className="text-end">Line Total</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {refundDetails.map((item, index) => {
                    const unitPrice = parseFloat(item.product_price || 0);
                    const qty = parseInt(item.product_qty || 1);
                    const lineTotal = parseFloat(item.total_price || unitPrice * qty);
                    const discount = parseFloat(item.discount_given || 0);
                    const isItemRefunded =
                      Number(item.is_item_refunded) === 1 ||
                      isOrderFullyRefunded;

                    return (
                      <tr key={index}>
                        <td className="text-muted fw-semibold">{index + 1}</td>
                        <td>
                          <div className="fw-bold text-dark">{item.product_name}</div>
                          <span className="text-muted small">
                            {item.sku ? `Item code: ${item.sku} · ` : ""}
                            Unit Price: Rs. {unitPrice.toLocaleString()}
                          </span>
                        </td>
                        <td className="text-center fw-semibold text-dark">
                          <span className="badge bg-light text-dark border px-2 py-1">
                            {qty}x
                          </span>
                        </td>
                        <td className="text-end text-muted font-monospace">
                          Rs. {unitPrice.toLocaleString()}
                        </td>
                        <td className="text-end text-danger font-monospace">
                          {discount > 0 ? `- Rs. ${discount.toLocaleString()}` : "—"}
                        </td>
                        <td className="text-end fw-bold text-dark font-monospace">
                          Rs. {lineTotal.toLocaleString()}
                        </td>
                        <td className="text-center">
                          {isItemRefunded ? (
                            <span className="badge bg-danger bg-opacity-10 text-danger border border-danger-subtle px-2 py-1">
                              <FaCheckCircle className="me-1" size={10} /> Refunded
                            </span>
                          ) : (
                            <span className="badge bg-secondary bg-opacity-10 text-secondary border px-2 py-1">
                              Active (Not Refunded)
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Refund Transaction History Card */}
          {refundTransactions.length > 0 && (
            <div className="admin-card mb-4">
              <div className="admin-card-header bg-white d-flex align-items-center justify-content-between">
                <h5 className="admin-card-title d-flex align-items-center gap-2 mb-0">
                  <FaReceipt className="text-danger" /> Processed Refund Transactions ({refundTransactions.length})
                </h5>
                <span className="badge bg-success bg-opacity-10 text-success fw-semibold">
                  Reconciled in General Ledger
                </span>
              </div>
              <div className="table-responsive">
                <table className="admin-table mb-0">
                  <thead>
                    <tr>
                      <th>Refund Number</th>
                      <th>Reason / Memo</th>
                      <th>Restock Status</th>
                      <th>Processed Date</th>
                      <th className="text-end">Disbursed Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refundTransactions.map((tx, idx) => (
                      <tr key={idx}>
                        <td className="fw-bold text-dark font-monospace">{tx.refund_no}</td>
                        <td className="text-muted small">{tx.reason || "Customer Return"}</td>
                        <td>
                          {tx.restock_inventory ? (
                            <span className="badge bg-success bg-opacity-10 text-success fw-semibold">
                              Restocked to Inventory
                            </span>
                          ) : (
                            <span className="badge bg-secondary bg-opacity-10 text-secondary fw-semibold">
                              No Restock
                            </span>
                          )}
                        </td>
                        <td className="text-muted small">
                          {moment(tx.created_at).isValid() ? moment(tx.created_at).format("DD MMM YYYY, hh:mm A") : "—"}
                        </td>
                        <td className="text-end fw-bold text-danger font-monospace">
                          Rs. {parseFloat(tx.amount).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Financial Breakdown Card */}
          <div className="admin-card mb-4">
            <div className="admin-card-header bg-white">
              <h5 className="admin-card-title d-flex align-items-center gap-2">
                <FaReceipt className="text-primary" /> Financial Disbursement Summary
              </h5>
            </div>
            <div className="admin-card-body">
              <div className="d-flex justify-content-between py-2 border-bottom">
                <span className="text-muted">Order Grand Total</span>
                <span className="fw-semibold text-dark font-monospace">
                  Rs. {parseFloat(orderGrandTotal || totalCost).toLocaleString()}
                </span>
              </div>
              <div className="d-flex justify-content-between py-2 border-bottom">
                <span className="text-muted">Payment Provider Settlement</span>
                <span className="fw-semibold text-success font-monospace">
                  {order?.payment_mode || "COD / Cash"}
                </span>
              </div>
              <div className="d-flex justify-content-between py-2 border-bottom">
                <span className="text-muted">Accounting Status</span>
                <span className="badge bg-success bg-opacity-10 text-success fw-semibold px-2 py-1">
                  Balanced Double-Entry Journal Posted
                </span>
              </div>
              {remainingRefundable > 0 && (
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span className="text-muted">Remaining Balance (Not Refunded)</span>
                  <span className="fw-semibold text-primary font-monospace">
                    Rs. {parseFloat(remainingRefundable).toLocaleString()}
                  </span>
                </div>
              )}
              <div className="d-flex justify-content-between pt-3">
                <span className="fw-bold fs-5 text-dark">Total Amount Disbursed to Customer</span>
                <span className="fw-bold fs-5 text-danger font-monospace">
                  Rs. {parseFloat(totalRefunded > 0 ? totalRefunded : totalCost).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Chronological Event Timeline */}
          <div className="admin-card mb-4 shadow-sm">
            <div className="admin-card-header bg-white d-flex align-items-center justify-content-between py-3">
              <h5 className="admin-card-title d-flex align-items-center gap-2 mb-0">
                <FaHistory className="text-primary" /> Chronological Event Timeline
              </h5>
              <span className="badge rounded-pill bg-light text-dark border">
                {timeline.length} {timeline.length === 1 ? "Event" : "Events"} Logged
              </span>
            </div>
            <div className="admin-card-body p-4">
              {timeline.length === 0 ? (
                <div className="text-center text-muted py-3 small">
                  <FaHistory size={24} className="opacity-50 mb-2" />
                  <p className="mb-0">No timeline events recorded yet.</p>
                </div>
              ) : (
                <div className="position-relative ps-4" style={{ borderLeft: "2px solid #e2e8f0" }}>
                  {timeline.map((event, idx) => {
                    const isRefund =
                      event.event_type === "REFUND_PROCESSED" ||
                      event.event_type === "ORDER_REFUNDED" ||
                      Boolean(event.metadata?.refund_no);
                    const isCompleted =
                      event.event_type === "ORDER_COMPLETED" ||
                      event.event_type === "ORDER_DELIVERED";
                    const isDispatched =
                      event.event_type === "ORDER_DISPATCHED" ||
                      event.event_type === "ORDER_FULFILLED";
                    const isCancelled = event.event_type === "ORDER_CANCELLED";

                    const markerColor = isRefund
                      ? "#ef4444"
                      : isCompleted
                      ? "#10b981"
                      : isDispatched
                      ? "#f59e0b"
                      : isCancelled
                      ? "#6b7280"
                      : "#2563eb";

                    const formatEventDate = (dt) => {
                      if (!dt || dt.startsWith("0000")) return "—";
                      return moment(dt).isValid() ? moment(dt).format("DD MMM YYYY, hh:mm A") : dt;
                    };

                    return (
                      <div key={idx} className="mb-4 position-relative">
                        {/* Dot Marker */}
                        <div
                          style={{
                            position: "absolute",
                            left: "-25px",
                            top: "4px",
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            background: markerColor,
                            border: "3px solid #ffffff",
                            boxShadow: `0 0 0 2px ${markerColor}33`,
                          }}
                        ></div>

                        <div className="d-flex justify-content-between align-items-start mb-1 flex-wrap gap-2">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                              {event.title}
                            </span>
                            {isRefund && (
                              <span
                                className="badge bg-danger bg-opacity-10 text-danger border border-danger-subtle fw-semibold"
                                style={{ fontSize: "11px" }}
                              >
                                <FaUndoAlt className="me-1" size={9} />
                                {event.metadata?.refund_type || (event.metadata?.is_partial ? "Partial Refund" : "Refund Disbursed")}
                              </span>
                            )}
                            {isCompleted && (
                              <span
                                className="badge bg-success bg-opacity-10 text-success border border-success-subtle fw-semibold"
                                style={{ fontSize: "11px" }}
                              >
                                Delivered
                              </span>
                            )}
                          </div>
                          <small className="text-muted" style={{ fontSize: "11.5px" }}>
                            {formatEventDate(event.created_at)}
                          </small>
                        </div>

                        <p className="text-muted mb-1" style={{ fontSize: "12.5px" }}>
                          {event.description}
                        </p>

                        {/* Rich Refund Details Card */}
                        {isRefund && (
                          <div
                            className="p-3 my-2 rounded-3 border bg-light bg-opacity-50"
                            style={{ borderColor: "#fecaca" }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 pb-2 border-bottom">
                              <div>
                                <span className="text-muted small d-block">Refund Transaction Reference:</span>
                                <span className="fw-bold text-dark font-monospace">
                                  {event.metadata?.refund_no || "REF-TRANSACTION"}
                                </span>
                              </div>
                              <div className="text-end">
                                <span className="text-muted small d-block">Disbursed Amount:</span>
                                <span className="fw-bold fs-6 text-danger font-monospace">
                                  Rs. {Number(event.metadata?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </span>
                              </div>
                            </div>

                            <div className="row g-2 mb-2 small">
                              <div className="col-12 col-md-6">
                                <span className="text-muted">Reason / Memo: </span>
                                <span className="fw-semibold text-dark">
                                  {event.metadata?.reason || "Customer Return / Admin Refund"}
                                </span>
                              </div>
                              <div className="col-12 col-md-6 text-md-end">
                                <span className="text-muted">Inventory Action: </span>
                                {event.metadata?.restock_inventory !== false ? (
                                  <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle">
                                    Restocked to Warehouse
                                  </span>
                                ) : (
                                  <span className="badge bg-secondary bg-opacity-10 text-secondary border">
                                    No Restock
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Refunded Items Breakdown */}
                            {Array.isArray(event.metadata?.items) && event.metadata.items.length > 0 && (
                              <div className="mt-2 pt-2 border-top">
                                <span className="text-muted small fw-semibold d-block mb-1">
                                  Refunded Items Affected:
                                </span>
                                <div className="d-flex flex-column gap-1">
                                  {event.metadata.items.map((item, itmIdx) => (
                                    <div
                                      key={itmIdx}
                                      className="d-flex justify-content-between align-items-center bg-white px-2 py-1 rounded border small"
                                    >
                                      <span className="fw-medium text-dark">
                                        {item.product_name || `Item #${item.product_id || itmIdx + 1}`}
                                        {item.qty ? ` × ${item.qty}` : ""}
                                      </span>
                                      <span className="fw-semibold text-danger font-monospace">
                                        Rs. {Number(item.total_price || item.amount || 0).toLocaleString()}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Ledger & Remaining Info */}
                            <div
                              className="mt-2 pt-2 border-top d-flex justify-content-between align-items-center flex-wrap gap-2 text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              <span className="d-flex align-items-center gap-1">
                                ⚖️ {event.metadata?.ledger_reconciliation || "Balanced Double-Entry Journal Posted"}
                              </span>
                              {event.metadata?.remaining_refundable !== undefined && (
                                <span className="badge bg-white text-muted border">
                                  Remaining Refundable: Rs. {Number(event.metadata.remaining_refundable).toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="mt-1 d-flex align-items-center gap-2">
                          <small className="badge bg-light text-muted border" style={{ fontSize: "10.5px" }}>
                            Logged by {event.actor_name || "System"} ({event.actor_type || "SYSTEM"})
                          </small>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Customer & Order Metadata */}
        <div className="col-lg-4">
          {/* Customer Card */}
          <div className="admin-card mb-4">
            <div className="admin-card-header bg-white">
              <h5 className="admin-card-title d-flex align-items-center gap-2">
                <FaUser className="text-primary" /> Customer Profile
              </h5>
            </div>
            <div className="admin-card-body">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div
                  className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center fw-bold fs-5"
                  style={{ width: "48px", height: "48px" }}
                >
                  {customerName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h6 className="fw-bold text-dark mb-0">{customerName}</h6>
                  <span className="text-muted small">Verified Buyer</span>
                </div>
              </div>

              <div className="d-flex flex-column gap-2.5 small">
                {order?.email && (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <FaEnvelope className="text-primary flex-shrink-0" />
                    <span className="text-truncate">{order.email}</span>
                  </div>
                )}
                {order?.phone && (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <FaPhone className="text-success flex-shrink-0" />
                    <span>{order.phone}</span>
                  </div>
                )}
                {order?.address && (
                  <div className="d-flex align-items-start gap-2 text-muted mt-1">
                    <FaMapMarkerAlt className="text-danger flex-shrink-0 mt-1" />
                    <span>
                      {order.address}, {order.city} {order.state && `, ${order.state}`} {order.zipcode}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Order Details Card */}
          <div className="admin-card">
            <div className="admin-card-header bg-white">
              <h5 className="admin-card-title d-flex align-items-center gap-2">
                <FaShieldAlt className="text-success" /> Order Information
              </h5>
            </div>
            <div className="admin-card-body small">
              <div className="d-flex justify-content-between py-1.5 border-bottom">
                <span className="text-muted">Tracking No</span>
                <span className="fw-bold text-dark font-monospace">
                  {order?.tracking_no || "—"}
                </span>
              </div>
              <div className="d-flex justify-content-between py-1.5 border-bottom">
                <span className="text-muted">Order ID</span>
                <span className="fw-semibold text-dark">#{orderId}</span>
              </div>
              <div className="d-flex justify-content-between py-1.5 border-bottom">
                <span className="text-muted">Payment Mode</span>
                <span className="fw-semibold text-dark">
                  {order?.payment_mode || "COD"}
                </span>
              </div>
              <div className="d-flex justify-content-between py-1.5 border-bottom">
                <span className="text-muted">Created Date</span>
                <span className="text-dark">
                  {moment(order?.created_at).isValid() ? moment(order?.created_at).format("DD MMM YYYY, hh:mm A") : "—"}
                </span>
              </div>
              <div className="d-flex justify-content-between pt-2">
                <span className="text-muted">Refund Trace</span>
                <span className="badge bg-danger bg-opacity-10 text-danger fw-semibold">
                  Refunded to Customer
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
